<template>
  <CModal
    title="Art Request Actions"
    color="info"
    size="xl"
    :show="show"
    @update:show="emitShow"
  >
    <div v-if="loading">Loading art request...</div>
    <div v-else-if="item">
      <div class="detail-grid">
        <div><strong>Order:</strong> <router-link v-if="item.order_id" :to="'/order/' + item.order_id">{{ item.order_id }}</router-link><span v-else>—</span></div>
        <div><strong>Customer:</strong> {{ item.customer_number || '—' }}</div>
        <div><strong>Program:</strong> {{ item.program_name || '—' }}</div>
        <div><strong>Section:</strong> {{ item.section_name || '—' }}</div>
        <div><strong>Bundle:</strong> {{ item.bundle_name || '—' }}</div>
        <div><strong>Placement:</strong> {{ item.bundle_placement_name || '—' }}</div>
        <div><strong>Technique:</strong> {{ item.technique_name || '—' }}</div>
        <div><strong>Location:</strong> {{ formatLocation(item) }}</div>
        <div><strong>Artwork:</strong> {{ item.artwork_name || '—' }}</div>
        <div><strong>Units:</strong> {{ item.units || 0 }}</div>
      </div>

      <CRow>
        <CCol sm="12" md="6">
          <label class="field-label">Request Type</label>
          <v-select
            v-model="form.request_type"
            :options="requestTypeOptions"
            label="label"
            :reduce="option => option.value"
          />
        </CCol>
        <CCol sm="12" md="6">
          <label class="field-label">Status</label>
          <v-select
            v-model="form.status"
            :options="statusOptions"
            label="label"
            :reduce="option => option.value"
          />
        </CCol>
      </CRow>

      <CTextarea
        label="Queue Notes"
        rows="3"
        v-model="form.notes"
      />

      <div class="action-row">
        <CButton size="sm" color="light" @click="setStatus('in_review')">Mark In Review</CButton>
        <CButton size="sm" color="warning" @click="setStatus('ready_to_submit')">Mark Ready</CButton>
        <CButton size="sm" color="info" @click="setStatus('submitted_to_workflow')">Mark Submitted</CButton>
        <CButton size="sm" color="success" @click="setStatus('completed')">Complete</CButton>
      </div>

      <div class="detail-block">
        <div class="detail-heading">Workflow Handoff</div>
        <component-request
          v-if="form.request_type === 'component'"
          :new-data="handoffData"
          :group="handoffGroup"
          @submitted="workflowSubmitted"
        />
        <embroidery-request
          v-else-if="form.request_type === 'embroidery'"
          :new-data="handoffData"
          :group="handoffGroup"
          @submitted="workflowSubmitted"
        />
        <artpack-request
          v-else-if="form.request_type === 'artpack'"
          :new-data="handoffData"
          :group="handoffGroup"
          @submitted="workflowSubmitted"
        />
        <div v-else class="helper-text">
          Select `Component`, `Embroidery`, or `Artpack` to open a prefilled workflow handoff form.
        </div>
      </div>
    </div>
    <template #footer>
      <CButton color="secondary" @click="emitShow(false)">Close</CButton>
      <CButton color="info" @click="save" :disabled="saving || !item">
        {{ saving ? 'Saving...' : 'Save Changes' }}
      </CButton>
    </template>
  </CModal>
</template>

<script>
import axios from 'axios'
import ArtpackRequest from './../flow/artpack_request'
import ComponentRequest from './../flow/component_request'
import EmbroideryRequest from './../flow/embroidery_request'

export default {
  name: 'ArtRequestActionModal',
  components: {
    ArtpackRequest,
    ComponentRequest,
    EmbroideryRequest
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    queueId: {
      type: [String, Number],
      default: ''
    }
  },
  data () {
    return {
      loading: false,
      saving: false,
      item: null,
      form: {
        status: '',
        request_type: '',
        notes: ''
      },
      statusOptions: [
        { label: 'New', value: 'new' },
        { label: 'In Review', value: 'in_review' },
        { label: 'Ready To Submit', value: 'ready_to_submit' },
        { label: 'Submitted To Workflow', value: 'submitted_to_workflow' },
        { label: 'Completed', value: 'completed' },
        { label: 'Cancelled', value: 'cancelled' }
      ],
      requestTypeOptions: [
        { label: 'Artwork', value: 'artwork' },
        { label: 'Embroidery', value: 'embroidery' },
        { label: 'Component', value: 'component' },
        { label: 'Artpack', value: 'artpack' }
      ]
    }
  },
  computed: {
    handoffData () {
      if (!this.item) return {}
      const payload = this.item.payload || {}
      return Object.assign({}, payload, {
        order_id: this.item.order_id,
        notes: this.form.notes || this.item.notes || '',
        request_type: this.form.request_type,
        program_name: this.item.program_name,
        section_name: this.item.section_name,
        bundle_name: this.item.bundle_name,
        bundle_placement_name: this.item.bundle_placement_name,
        technique_name: this.item.technique_name,
        location_code: this.item.location_code,
        location_name: this.item.location_name,
        artwork_name: this.item.artwork_name,
        units: this.item.units
      })
    },
    handoffGroup () {
      return {
        cc_number: this.item && this.item.cc_number ? this.item.cc_number : '',
        as400_log_number: this.item && this.item.log_number ? this.item.log_number : ''
      }
    }
  },
  watch: {
    show (val) {
      if (val && this.queueId) {
        this.load()
      }
    },
    queueId (val) {
      if (val && this.show) {
        this.load()
      }
    }
  },
  methods: {
    emitShow (value) {
      this.$emit('update:show', value)
    },
    load () {
      if (!this.queueId) return
      this.loading = true
      axios.get(this.$apiAdress + '/api/art_request_queues/' + this.queueId + '?token=' + localStorage.getItem('api_token'))
        .then((response) => {
          this.item = response.data || null
          this.form.status = this.item ? (this.item.status || 'new') : ''
          this.form.request_type = this.item ? (this.item.request_type || 'artwork') : ''
          this.form.notes = this.item ? (this.item.notes || '') : ''
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.$store.commit('axios_error', error)
        })
    },
    setStatus (status) {
      this.form.status = status
    },
    workflowSubmitted () {
      this.form.status = 'submitted_to_workflow'
      this.save()
    },
    save () {
      if (!this.item || !this.item.id) return
      this.saving = true
      axios.post(this.$apiAdress + '/api/art_request_queues/save?token=' + localStorage.getItem('api_token'), {
        id: this.item.id,
        status: this.form.status,
        request_type: this.form.request_type,
        notes: this.form.notes
      }).then((response) => {
        this.item = response.data && response.data.item ? response.data.item : this.item
        this.saving = false
        this.$emit('saved', this.item)
      }).catch((error) => {
        this.saving = false
        this.$store.commit('axios_error', error)
      })
    },
    formatLocation (item) {
      if (!item) return '—'
      return [item.location_code, item.location_name].filter(Boolean).join(' - ') || '—'
    }
  }
}
</script>

<style scoped>
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  margin-bottom: 16px;
}

.field-label {
  font-weight: 600;
}

.action-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 12px 0 16px;
}

.detail-block {
  padding: 12px;
  border-radius: 10px;
  background: #f6f9fc;
  border: 1px solid #deebf6;
  max-height: 60vh;
  overflow: auto;
}

.detail-heading {
  font-weight: 700;
  margin-bottom: 8px;
}

.helper-text {
  font-size: 12px;
  color: #7d8fa3;
}
</style>
